import { baseApi } from './baseApi';

/**
 * The backend returns jobs grouped by job. Each job may have multiple referrals.
 * We flatten so each referral = one card.
 *
 * For "sent jobs" (direction='their'): each referral is a different candidate
 * the current user referred.
 * For "my jobs" (direction='my'): each referral is a different person who
 * referred the current user for that job.
 */
const flattenJobs = (jobs) => {
  if (!Array.isArray(jobs)) return jobs;

  const flat = [];
  jobs.forEach((job) => {
    if (job.referrals?.length > 0) {
      job.referrals.forEach((referral) => {
        flat.push({ ...job, referrals: [referral] });
      });
    } else {
      flat.push(job);
    }
  });
  return flat;
};

export const trackerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Board / List data ───────────────────────────────────
    getTrackerJobs: builder.query({
      query: ({ direction, status }) => ({
        url: `/applicants/jobs_tracker`,
        params: { direction, status },
      }),
      providesTags: (result, error, { direction, status }) => [
        { type: 'Tracker', id: `${direction}-${status}` },
      ],
      // Response shape: { payload: { jobs: [...] } }
      // Only flatten for sent referrals (direction='their') where each referral
      // is a different candidate and needs its own card. For received referrals
      // (direction='my'), keep jobs grouped so the card can show a referral-
      // provider count badge ("+N").
      transformResponse: (response, meta, arg) => {
        const jobs = response?.payload?.jobs || response?.jobs || [];
        return {
          jobs: arg.direction === 'their' ? flattenJobs(jobs) : jobs,
        };
      },
    }),

    // ─── Server-side search ──────────────────────────────────
    searchTrackerJobs: builder.query({
      query: ({ query }) => ({
        url: `/applicants/jobs_tracker_search`,
        params: { query },
      }),
      providesTags: ['TrackerSearch'],
      // Response shape: { payload: { results: [{type:'my_jobs', data:[...]}, {type:'their_jobs', data:[...]}] } }
      // Only flatten sent jobs (theirJobs) — received jobs stay grouped so cards
      // retain the referral-provider count badge.
      transformResponse: (response) => {
        const results = response?.payload?.results || [];
        const myJobs = results.find((r) => r.type === 'my_jobs')?.data || [];
        const theirJobs = results.find((r) => r.type === 'their_jobs')?.data || [];
        return { myJobs, theirJobs: flattenJobs(theirJobs) };
      },
    }),

    getInterviewStages: builder.query({
      query: ({ organizationId }) => ({
        url: `/organizations/${organizationId}/fetch_interview_stages`,
      }),
      providesTags: ['InterviewStages'],
      transformResponse: (response) => response?.payload?.interview_stages || response?.payload || [],
    }),

    // ─── Detail view ─────────────────────────────────────────
    getTrackerJobDetails: builder.query({
      query: ({ referralId, requestId, jobId, direction }) => ({
        url: `/applicants/jobs_tracker_details`,
        params: {
          referral_id: referralId,
          request_id: requestId,
          job_id: jobId,
          direction,
        },
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'TrackerDetail', id: jobId }],
      // BoonResponse shape: { response_code: 0, payload: { job: {...}, candidate: {...} } }
      // When response_code !== 0, payload is empty {}
      transformResponse: (response) => {
        // BoonResponse error — payload is empty, treat as null
        if (response?.response_code && response.response_code !== 0) {
          return { job: null, candidate: null };
        }
        const job = response?.payload?.job;
        // If job exists but has no id/title, the API returned empty data
        if (!job || (!job.id && !job.title)) {
          return { job: null, candidate: null };
        }
        return {
          job,
          candidate: response?.payload?.candidate || null,
          strengthenQuestions: response?.payload?.strengthen_questions || null,
        };
      },
    }),

    getReferralStrengthQuestions: builder.query({
      query: ({ referRequestId, reStrengthenReferral = true }) => ({
        url: `/strength_questions/${referRequestId}`,
        params: { re_strengthen_referral: reStrengthenReferral },
      }),
      transformResponse: (response) => response?.payload || response,
    }),

    getTrackerReferUsers: builder.query({
      query: ({ jobId }) => ({
        url: `/refer_users`,
        params: { job_id: jobId },
      }),
      transformResponse: (response) => response?.payload || response,
    }),

    // ─── Mutations ───────────────────────────────────────────
    markTrackerSeen: builder.mutation({
      query: ({ source, referRequestsIds }) => ({
        url: `/update_tracker_seen_status/`,
        method: 'PUT',
        body: { source, refer_requests_ids: referRequestsIds },
      }),
    }),

    declineTrackerJob: builder.mutation({
      query: ({ jobId }) => ({
        url: `/applicants/decline_job`,
        method: 'POST',
        body: { job_id: jobId },
      }),
      invalidatesTags: ['Tracker'],
    }),
  }),
});

export const {
  useGetTrackerJobsQuery,
  useSearchTrackerJobsQuery,
  useGetInterviewStagesQuery,
  useGetTrackerJobDetailsQuery,
  useGetReferralStrengthQuestionsQuery,
  useGetTrackerReferUsersQuery,
  useMarkTrackerSeenMutation,
  useDeclineTrackerJobMutation,
} = trackerApi;
